import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { Router, UrlTree } from '@angular/router';
import { TxSession } from '../../shared/interfaces/session';
import { Observable } from 'rxjs';
import { AppConfigService } from '../../shared/services/appconfig.service';
import { GlobalConstants } from '../../globalConstants';
import sha1 from 'crypto-js/sha1';
import { TerminalService } from '../../shared/services/terminal.service';
import { Store } from '@ngrx/store';
import { State } from '../../store/reducers';
import { performLogin, setSession, verifySession } from '../../store/actions/user.actions';
import { getSession } from '../../store/selectors/user.selectors';
import { delay, filter, map, take, tap, timeout, withLatestFrom } from 'rxjs/operators';
import { CdkDragEnd } from '@angular/cdk/drag-drop/drag-events';

@Component({
	selector: 'tx-login',
	templateUrl: './tx-login.template.html',
	encapsulation: ViewEncapsulation.None,
})
export class TxLoginComponent implements AfterViewInit {
	@HostBinding('class') class = 'login-page';
	@ViewChildren('username') vc;

	CurrentYear = new Date().getFullYear();

	errorText;
	loginData: any;
	errorState = false;
	mySession: TxSession;
	mySession$: Observable<TxSession>;
	config: any;
	wlVersion: string = GlobalConstants.appVersion;
	showPinLoginCard = false;
	pinCode = '';
	pinModeState = false;
	pinError = false;
	showQrCamera = false;
	moveQrCamera = false;
	
	// 2FA properties
	show2FAInput = false;
	twoFACode = '';
	twoFASessionId = '';
	twoFAStatusText = '';
	twoFAError = false;
	twoFAErrorMessage = '';

	constructor(
		private store: Store<State>,
		private myAuthService: AuthService,
		private myRouter: Router,
		private myConfigService: AppConfigService,
		public myTerminalService: TerminalService,
		private cdr: ChangeDetectorRef
	) {
		this.mySession$ = store.select(getSession).pipe(
			withLatestFrom(this.myTerminalService.pinLoggedIn),
			tap(([session, pinLoggedIn]) =>
				!!session && pinLoggedIn ? this.handlePinLoginResult(session) : this.handleLoginResult(session)
			),
			map(([session]) => session)
		);

		this.config = this.myConfigService.getConfig();
		if (localStorage.getItem('Frontpage') === null) {
			localStorage.setItem('Frontpage', 'buchungen');
		}

		if (localStorage.getItem('txlgn') != null) {
			this.loginData = JSON.parse(localStorage.getItem('txlgn'));
			if (this.loginData.password === '' || this.loginData.password === null) {
				this.callLogin(this.loginData.username, null, true);
			} else {
				this.callLogin(this.loginData.username, this.loginData.password, true);
			}
		}

		this.myTerminalService.pinLogin.subscribe((data) => {
			this.pinModeState = data;
			if (this.pinModeState) {
				this.showPinLoginCard = localStorage.getItem('showPinLoginCard') === '1';
			}
		});

		this.myTerminalService.qrQrPermanentCamera.pipe(take(1)).subscribe((data) => {
			this.showQrCamera = data;
		});
	}

	get pinLength() {
		return this.pinCode.length;
	}

	get pinEnabled() {
		return this.pinLength < 4;
	}

	qrCodeDragged(event: CdkDragEnd) {
		const point = event.source.element.nativeElement.getBoundingClientRect();
		this.myTerminalService.setQrCameraPosition(point);
		this.moveQrCamera = false;
	}

	callLogin(aUsername: string, aPassword: string, aLoggedIn?: boolean) {
		let loginAnfrage: Observable<TxSession>;
		loginAnfrage = this.myAuthService.login(aUsername, aPassword, aLoggedIn);
		loginAnfrage.subscribe(
			(mySessionData) => {
				this.mySession = mySessionData;
				this.handleLoginResult(mySessionData);
			},
			(error) => {
				console.error(error);
			}
		);
	}

	callQrLogin(qr: string | null, permanent = false) {
		this.showQrCamera = false;
		this.myAuthService.getSessionDomain().then((mySessionDomain) => {
			let loginAnfrage: Observable<TxSession>;
			loginAnfrage = this.myAuthService.qrLogin(qr, mySessionDomain);
			loginAnfrage.subscribe(
				(mySessionData) => {
					this.mySession = mySessionData;
					this.handlePinLoginResult(mySessionData);
				},
				(error) => {
					console.error(error);
				},
				() => {
					if (permanent) {
						this.showQrCamera = true;
					}
				}
			);
		});
	}

	callPinLogin() {
		if (this.pinLength === 4) {
			this.myAuthService.getSessionDomain().then((mySessionDomain) => {
				let loginAnfrage: Observable<TxSession>;
				loginAnfrage = this.myAuthService.pinLogin(this.pinCode, mySessionDomain);
				loginAnfrage.subscribe(
					(mySessionData) => {
						this.mySession = mySessionData;
						this.handlePinLoginResult(mySessionData);
					},
					(error) => {
						console.error(error);
					}
				);
			});
		}
	}

	checkLogin(username, password, stayloggedin) {
		let passwordHash: string;
		let txLoginStorage: string;

		if (stayloggedin) {
			if (password === '') {
				passwordHash = null;
			} else {
				passwordHash = sha1(password).toString();
			}
			txLoginStorage = JSON.stringify({ username, password: passwordHash });
			localStorage.setItem('txlgn', txLoginStorage);
		} else {
			// If "stay logged in" is NOT checked, remove any existing stored credentials
			localStorage.removeItem('txlgn');
			localStorage.removeItem('currentSession');
		}
		this.store.dispatch(performLogin(username, password, false));
	}

	clickPinButton(pressedNumber: string | number) {
		if (this.pinLength < 4) {
			this.pinCode = this.pinCode + pressedNumber;
			this.callPinLogin();
		}
	}

	resetPinCode() {
		this.pinCode = '';
	}

	handleLoginResult(mySessionData) {
		// If session is null (e.g., after cancel), reset 2FA UI and show login form
		if (!mySessionData) {
			this.show2FAInput = false;
			this.twoFACode = '';
			this.twoFASessionId = '';
			this.twoFAStatusText = '';
			this.twoFAError = false;
			this.twoFAErrorMessage = '';
			return;
		}
		
		// Check if 2FA is required (statuscode -99 OR statustext contains "2FA")
		if (mySessionData.statuscode === -99 || mySessionData.statustext?.includes('2FA neccessary')) {
			// Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
			setTimeout(() => {
				this.show2FAInput = true;
				this.twoFASessionId = mySessionData.sessionid;
				this.twoFAStatusText = mySessionData.statustext || '';
				this.twoFAError = false;
				this.twoFAErrorMessage = '';
				this.cdr.detectChanges();
			}, 0);
			return;
		}
		
		if (mySessionData && mySessionData.statuscode === 0 && mySessionData.sessionid !== '') {
			//this.myTerminalService.pinLoggedInSubject.next(false);
			/*if (this.myAuthService.redirectUrl) {
				this.myRouter.navigate([this.myAuthService.redirectUrl]);
			}*/
			let successUrl: string =
				'/' + localStorage.getItem('Frontpage') ? localStorage.getItem('Frontpage') : 'buchungen';
			// Redirect to new time tracking view if enabled
			if (successUrl === 'buchungen' && localStorage.getItem('txNewTimeTracking') === '1') {
				successUrl = 'buchungen-new';
			}
			this.myTerminalService.setTerminalMode(mySessionData.terminal_mode);
			if (mySessionData.reset_pw === 1) {
				//} && environment.production) {
				successUrl = '/account';
			}
			if (this.myAuthService.redirectUrl) {
				successUrl = this.myAuthService.redirectUrl;

				this.myAuthService.redirectUrl = '';

				this.myRouter.navigateByUrl(this.myRouter.parseUrl(successUrl));
			} else {
				this.myRouter.navigate([successUrl]);
			}
		}
	}

	handlePinLoginResult(mySessionData) {
		if (mySessionData.statuscode === 0 && mySessionData.sessionid !== '') {
			this.myTerminalService.pinLoggedInSubject.next(true);
			this.store.dispatch(verifySession(mySessionData));
			const pinUrl = localStorage.getItem('txNewTimeTracking') === '1' ? '/buchungen-new' : '/buchungen';
			this.myRouter.navigateByUrl(pinUrl);
		} else {
			this.pinError = true;
			setTimeout(() => {
				this.pinError = false;
				this.resetPinCode();
			}, 800);
		}
	}
	
	// Verify 2FA code
	verify2FACode() {
		if (!this.twoFACode || this.twoFACode.length === 0) {
			this.twoFAError = true;
			this.twoFAErrorMessage = 'Bitte geben Sie den 2FA-Code ein.';
			return;
		}
		
		this.myAuthService.verify2FA(this.twoFACode, this.twoFASessionId).subscribe(
			(response) => {
				if (response && response.statuscode === 0) {
					// 2FA verification successful, proceed with login
					this.show2FAInput = false;
					this.twoFACode = '';
					this.twoFAError = false;
					
					// Get the current session from store and update it with statuscode 0
					this.store.select(getSession).pipe(take(1)).subscribe((session) => {
						if (session) {
							// Update session with statuscode 0 to complete login
							const updatedSession = {
								...session,
								statuscode: 0,
								statustext: 'OK'
							};
							
							// Store session in sessionStorage for current browser session
							sessionStorage.setItem('currentSession', JSON.stringify(updatedSession));
							
							// If "stay logged in" was checked, also store in localStorage
							if (localStorage.getItem('txlgn')) {
								localStorage.setItem('currentSession', JSON.stringify(updatedSession));
							}
							
							// Dispatch verifySession to update store
							this.store.dispatch(verifySession(updatedSession));
							
							// Wait for store update, then navigate
							setTimeout(() => {
								let successUrl: string = localStorage.getItem('Frontpage') || 'buchungen';
								// Redirect to new time tracking view if enabled
								if (successUrl === 'buchungen' && localStorage.getItem('txNewTimeTracking') === '1') {
									successUrl = 'buchungen-new';
								}
								this.myTerminalService.setTerminalMode(session.terminal_mode);
								
								if (session.reset_pw === 1) {
									successUrl = 'account';
								}
								
								if (this.myAuthService.redirectUrl) {
									successUrl = this.myAuthService.redirectUrl;
									this.myAuthService.redirectUrl = '';
								}
								
								console.log('2FA successful, navigating to:', successUrl);
								this.myRouter.navigate([successUrl]);
							}, 100);
						}
					});
				} else {
					// 2FA verification failed
					this.twoFAError = true;
					this.twoFAErrorMessage = response?.statustext || 'Ungültiger 2FA-Code. Bitte versuchen Sie es erneut.';
				}
			},
			(error) => {
				console.error('2FA verification error:', error);
				this.twoFAError = true;
				if (error?.status === 404) {
					this.twoFAErrorMessage = 'Der 2FA-Endpoint ist auf dem Server noch nicht verfügbar (404). Bitte kontaktieren Sie Sebastian.';
				} else {
					this.twoFAErrorMessage = 'Fehler bei der 2FA-Verifizierung. Bitte versuchen Sie es erneut.';
				}
			}
		);
	}
	
	// Reset 2FA input and clear session
	reset2FA() {
		this.show2FAInput = false;
		this.twoFACode = '';
		this.twoFASessionId = '';
		this.twoFAError = false;
		this.twoFAErrorMessage = '';
		this.twoFAStatusText = '';
		
		// Clear session from store
		this.store.dispatch(setSession(null));
		
		// Clear session storage (current session only)
		sessionStorage.removeItem('currentSession');
		
		// Do NOT remove 'txlgn' or localStorage 'currentSession' - only remove on explicit logout
	}

	ngAfterViewInit(): void {
		this.myTerminalService.pinLogin.subscribe((data) => {
			this.pinModeState = data;
			if (this.pinModeState) {
				this.showPinLoginCard = localStorage.getItem('showPinLoginCard') === '1';
			} else {
				this.vc.first.nativeElement.focus();
			}
		});
	}

	toggleShowPinLoginCard() {
		this.showPinLoginCard = !this.showPinLoginCard;
		localStorage.setItem('showPinLoginCard', this.showPinLoginCard ? '1' : '0');
	}
}
